<template>
  <Popover popoverClass="numeric-input" trigger="click focus" :disabled="!isKeypad || disabled" @hide="initFocus">
    <TextInput
      v-bind="$attrs"
      v-on="$listeners"
      ref="inputText"
      :type="isSP || formatter !== null ? 'tel' : 'number'"
      :value="formatter !== null ? formatter(updatedValue) : updatedValue"
      :readonly="isKeypad"
      :disabled="disabled"
      @input="onUpdate($event)"
      @focus="e => isKeypad && e.target.blur()"
      @compositionstart="isComposing = true"
      @compositionend="e => onCompositionEnd(e)"
      @blur="onBlur"
      @keydown="onKeyDown"
      @paste.prevent="event => onPaste(event)"
    />
    <NumericKeypad slot="popover" v-model="updatedValue" @change="handleChangeFocus" changed :resettable="false" />
  </Popover>
</template>

<script>
import { mapState } from 'vuex'
import { isWebView } from '@/utils/device'
import TextInput from '@/components/atoms/TextInput'
import NumericKeypad from '@/components/molecules/NumericKeypad'
import Popover from '@/components/molecules/Popover'

/**
 * 数値入力
 */
export default {
  name: 'NumericInput',
  components: {
    TextInput,
    NumericKeypad,
    Popover,
  },
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    /** 値 (v-modal) */
    value: [Number, String],
    /** 文字列フォーマット */
    formatter: { type: Function, default: null },
    /** 非活性 */
    disabled: Boolean,
    /** 最大桁数 */
    maxDigit: { type: Number, default: null },
    /** 最大値 */
    maxValue: { type: Number, default: null },
    /** inputとして扱う */
    asInput: Boolean,
    /** 初回入力時にリセットするか */
    overWrite: { type: Boolean, default: true },
    /** デフォルト値。0以外が設定されている場合入力項目は自然数しか入らなくなる。 */
    defaultVal: {
      type: String,
      default: null,
      validator: value => `${Math.abs(value)}` === value, // 整数値であることのチェック
    },
  },
  data() {
    return {
      changed: false,
      lastKey: '',
      isComposing: false,
    }
  },
  computed: {
    ...mapState({
      isSP: state => state.app.isSP,
    }),
    updatedValue: {
      get() {
        return this.value
      },
      set(value) {
        // deleteボタンが押されていない場合
        if (value.length > 0 && String(this.value).length < value.length) {
          // onKeyDownの処理を再現
          this.lastKey = value[value.length - 1]
        }
        this.onUpdate(value)
      },
    },
    isKeypad() {
      return isWebView() && !this.asInput
    },
  },
  methods: {
    initFocus() {
      const el = this.$refs.inputText.$el
      if (el) {
        el.focus()
        el.scrollLeft = 0
      }
      this.lastKey = null
      this.changed = false
      this.$emit('onClose')
    },
    handleChangeFocus() {
      const el = this.$refs.inputText.$el
      if (el) {
        this.$nextTick(() => {
          el.focus()
          el.scrollLeft = el.scrollWidth
        })
      }
    },
    onKeyDown($event) {
      const key = $event.key
      if (['ArrowUp', 'ArrowDown', 'e', '+', '-', '.'].includes(key)) {
        $event.preventDefault()
        return
      }

      if ('1234567890'.includes(key)) {
        if (
          this.changed &&
          ((this.maxDigit !== null && this.maxDigit <= `${this.value}`.length) ||
            (this.maxValue !== null && this.maxValue <= this.value))
        ) {
          $event.preventDefault()
        }
        this.lastKey += key
      }
    },
    onCompositionEnd(e) {
      this.isComposing = false
      this.onUpdate(e.target.value)
    },
    onUpdate(value) {
      if (this.isComposing) {
        return
      }
      let formatValue = value.replace(/[^0-9]/g, '')
      switch (true) {
        // 初期化処理
        case this.defaultVal !== null && (formatValue === '' || !+formatValue):
          formatValue = this.defaultVal
          this.lastKey = ''
          this.changed = false
          break
        // 未変更の場合
        case !this.changed:
          // 入力された値が前回の値と一致しない場合
          if (this.overWrite && this.lastKey !== '' && this.lastKey !== String(this.value)) {
            // デフォルト値が設定されている且つ0が入力された時以外
            if (!(this.defaultVal !== null && this.lastKey === '0')) {
              // 「2」の状態で「2」以外を入力するとクリアされる
              formatValue = this.lastKey
            }
          }
          this.changed = true
          break
        // 変更済みの場合
        default:
          // 何もしない
          break
      }
      if (this.maxDigit !== null) formatValue = formatValue.slice(0, this.maxDigit)
      if (this.maxValue !== null && this.maxValue <= +formatValue) formatValue = this.maxValue
      if (!this.isComposing) {
        this.$emit('update', formatValue)
        // updateがemitした時domが更新されないケースが有るため、明示的に更新する。
        this.$forceUpdate()
      }
    },
    onPaste(event) {
      const clipboardData = event.clipboardData
      if (clipboardData !== null) {
        this.$nextTick(() => {
          this.onUpdate(clipboardData.getData('text/plain'))
        })
      }
    },
    onBlur() {
      if (!this.isKeypad) {
        this.lastKey = ''
        this.changed = false
        this.$emit('onClose')
      }
    },
  },
}
</script>

<style scoped lang="postcss">
input[type='tel'],
input[type='number'] {
  -moz-appearance: textfield;
}
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
